import React, {Suspense, useEffect} from "react";
import {BrowserRouter as Router, useLocation} from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import {OpenFeatureProvider} from "@openfeature/react-sdk";
import Alerts from "./components/Alerts";
import ErrorBoundary from "./components/ErrorBoundary";
import Main from "./components/Main";
import RexAIProvider from "./context/RexAIProvider";
import AlertProvider from "./context/AlertProvider";
import {AuthzProvider} from "./context/AuthzProvider";
import {HomeProvider} from "./context/HomeProvider";
import {ConsolidatedThemeProvider} from "./context/ConsolidatedThemeProvider";
import {FeatureFlagProvider} from "./context/FeatureFlagProvider";
import {GraphQLClientProvider} from "./context/GraphQLClientProvider";
import {RegScoutGraphQLClientCacheProvider} from "./context/RegScoutGraphQLClientCacheProvider";
import {IdleTimerProvider} from "./context/IdleTimerProvider";
import {OrgSettingsProvider} from "./context/OrgSettingsProvider";
import {ThemeWrapperProvider, useTheme} from "./context/ThemeWrapperProvider";
import {AuthWrapper} from "./components/authentication/AuthWrapper";
import {NetworkErrorProvider} from "./components/errors/Context/NetworkErrorProvider";

import FeatureFlagInitializationBoundary from "./components/featureFlags/FeatureFlagInitializationBoundary";
import Loader from "./components/Loader";
import {SidebarProvider} from "./components/sidebar/Context/SidebarProvider";
import {TourProvider} from "./components/tour/Context/TourProvider";
import {UserProvider} from "./components/user/Context/UserProvider";
import {Sidebar} from "./components/sidebar/Sidebar";
import SidebarAgent from "./components/sidebar/SidebarAgent";
import {initializeFeatureFlags} from "./resources/featureFlags";
import {consoleTagline, initializeDayJS, initializeDOMPurify} from "./resources/environment";
import {initializeMetrics} from "./resources/metrics";
import PlatformRoutes from "./Routes";

initializeFeatureFlags();

export const isMeetingPath = (pathname) => pathname.split("/").includes("meeting");

const AppLayout = () => {
  const location = useLocation();
  const isMeetingPage = isMeetingPath(location.pathname);

  if (isMeetingPage) {
    return (
      <ErrorBoundary>
        <PlatformRoutes />
      </ErrorBoundary>
    );
  }

  return (
    <>
      <Sidebar />
      <Main>
        <SidebarAgent />
        <ErrorBoundary>
          <PlatformRoutes />
        </ErrorBoundary>
      </Main>
    </>
  );
};

const ApplicationProviders = () => (
  <FeatureFlagProvider>
    <NetworkErrorProvider>
      <AuthWrapper>
        <GraphQLClientProvider>
          <RegScoutGraphQLClientCacheProvider>
            <UserProvider>
              <AuthzProvider>
                <OrgSettingsProvider>
                  <HomeProvider>
                    <Router
                      future={{
                        v7_startTransition: true,
                      }}
                    >
                      <CssBaseline />
                      <AlertProvider>
                        <Alerts />
                        <IdleTimerProvider>
                          <RexAIProvider>
                            <TourProvider>
                              <SidebarProvider>
                                <AppLayout />
                              </SidebarProvider>
                            </TourProvider>
                          </RexAIProvider>
                        </IdleTimerProvider>
                      </AlertProvider>
                    </Router>
                  </HomeProvider>
                </OrgSettingsProvider>
              </AuthzProvider>
            </UserProvider>
          </RegScoutGraphQLClientCacheProvider>
        </GraphQLClientProvider>
      </AuthWrapper>
    </NetworkErrorProvider>
  </FeatureFlagProvider>
);

const App = () => {
  return (
    <ThemeWrapperProvider>
      <AppContent />
    </ThemeWrapperProvider>
  );
};

const AppContent = () => {
  const {activeTheme} = useTheme();

  useEffect(() => {
    console.log(consoleTagline);
    initializeDayJS();
    initializeDOMPurify();
    initializeMetrics();
  }, []);

  return (
    <ConsolidatedThemeProvider activeTheme={activeTheme}>
      <FeatureFlagInitializationBoundary
        fallback={(
          <OpenFeatureProvider suspend={false}>
            <ApplicationProviders />
          </OpenFeatureProvider>
        )}
      >
        <Suspense fallback={<Loader fullViewingHeight />}>
          <OpenFeatureProvider suspendUntilReady={true}>
            <ApplicationProviders />
          </OpenFeatureProvider>
        </Suspense>
      </FeatureFlagInitializationBoundary>
    </ConsolidatedThemeProvider>
  );
};

export default App;
